import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import Select from "../../../components/Select/Select";
import { FilterICMSTypes, filter, statusIcms } from "../models/Filters";
import TextFieldCustom from "../../../components/TextFieldCustom/TextFieldCustom";
import { GetRefundIcmsQuery } from "../../../modules/financial/models/GetRefundIcmsQuery";
import CustomButton, {
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import { format } from "date-fns";
import MultipleSelectDistributors from "../../../components/MultipleSelectDistributors/MultipleSelectDistributors";

import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import MultipleSelectCheckbox from "../../../components/MultipleSelectCheckbox";
import CalendarSingleInput from "../../../components/CalendarSingleInput";

interface ButtonSearch {
  dataFilter: (query: any) => void;
}

const FilterTableIcmsSection: React.FC<ButtonSearch> = ({ dataFilter }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { selectedDistributorsStoraged } = useDistributor();

  const [searchQuery, setSearchQuery] = useState<GetRefundIcmsQuery>({
    requestDate: undefined,
    status: [] as any,
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filterSelected, setFilterSelected] = useState(FilterICMSTypes.CNPJ);
  const [isCleanFilter, setIsCleanFilter] = useState(false);

  const isFilterInvalid = useMemo(() => {
    const hasDistributor = selectedDistributorsStoraged.length > 0;
    const hasId = !!searchQuery.refIds && searchQuery.refIds.length > 0;

    if (filterSelected === FilterICMSTypes.CNPJ) {
      return !hasDistributor;
    }

    if (filterSelected === FilterICMSTypes.ID) {
      return !hasId;
    }

    return false;
  }, [
    filterSelected,
    selectedDistributorsStoraged,
    searchQuery.refIds,
    selectedDate,
  ]);

  const handleSearchChange = useCallback((value: any, field: string) => {
    setSearchQuery((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsCleanFilter(false);
  }, []);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSearchQuery((prev) => ({
      ...prev,
      requestDate: date ? format(date, "dd-MM-yyyy") : undefined,
    }));
    setIsCleanFilter(false);
  };

  const handleConsult = useCallback(() => {
    const finalQuery: any = {};

    if (selectedDate) {
      finalQuery.requestDate = format(selectedDate, "dd-MM-yyyy");
    }

    const statusArray = Array.isArray(searchQuery.status)
      ? searchQuery.status
      : [];
    if (statusArray.length > 0) {
      finalQuery.status = statusArray.join(",");
    }

    if (filterSelected === FilterICMSTypes.CNPJ) {
      if (selectedDistributorsStoraged.length > 0) {
        finalQuery.customerCompanyIds = selectedDistributorsStoraged.join(",");
      }
    } else {
      if (searchQuery.refIds) {
        finalQuery.refIds = searchQuery.refIds;
      }
    }

    dataFilter(finalQuery);
  }, [
    searchQuery,
    filterSelected,
    selectedDistributorsStoraged,
    selectedDate,
    dataFilter,
  ]);

  const handleClearFilters = () => {
    setSelectedDate(null);
    setIsCleanFilter(true);

    const resetedState = {
      requestDate: undefined,
      status: [] as any,
      refIds: undefined,
    };

    setSearchQuery(resetedState);
    setFilterSelected(FilterICMSTypes.CNPJ);

    dataFilter({});

    setTimeout(() => setIsCleanFilter(false), 200);
  };

  const renderDynamicInput = useMemo(() => {
    const customRadius = isMobile ? "0.5rem" : "0 0.5rem 0.5rem 0";

    switch (filterSelected) {
      case FilterICMSTypes.CNPJ:
        return (
          <Box
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": { borderRadius: customRadius },
            }}
          >
            <MultipleSelectDistributors
              maxWidth={1200}
              label={"CNPJ"}
              clearSelection={isCleanFilter}
            />
          </Box>
        );
      case FilterICMSTypes.ID:
        return (
          <TextFieldCustom
            label="ID"
            placeholder="Informe o Id"
            value={searchQuery.refIds || ""}
            onChange={(e) =>
              handleSearchChange(e.target.value.replace(/\D/g, ""), "refIds")
            }
          />
        );
      default:
        return (
          <Box
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": { borderRadius: customRadius },
            }}
          >
            <MultipleSelectDistributors maxWidth={1200} label={"CNPJ"} />
          </Box>
        );
    }
  }, [
    filterSelected,
    searchQuery.refIds,
    isMobile,
    handleSearchChange,
    isCleanFilter,
  ]);

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <Grid container>
          <Grid item xs={12} sm={3}>
            <Select
              label="Buscar por"
              options={filter
                .filter(
                  (f) =>
                    f.value !== FilterICMSTypes.STATUS &&
                    f.value !== FilterICMSTypes.DATE
                )
                .map((item) => ({ option: item }))}
              value={filter.find((ele) => ele.value === filterSelected)}
              getOptionItemLabel={(v) => v.label}
              getSelectedOptionLabel={(v) => v.label}
              borderRadius={isMobile ? "0.5rem" : "0.5rem 0 0 0.5rem"}
              onChange={(v) => {
                setFilterSelected(v.value);
                setSearchQuery((prev) => ({
                  ...prev,
                  refIds: undefined,
                  customerCompanyIds: undefined,
                }));
              }}
              isDark
            />
          </Grid>

          <Grid item xs={12} sm={9}>
            {renderDynamicInput}
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={4}>
        <MultipleSelectCheckbox
          label="Status"
          maxWidth={1200}
          options={statusIcms.map((item) => ({
            value: item.value,
            label: item.label,
          }))}
          value={
            (Array.isArray(searchQuery.status)
              ? searchQuery.status
              : []) as string[]
          }
          onChange={(values) => handleSearchChange(values, "status")}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <CalendarSingleInput
          name="date"
          label="Data de solicitação"
          onChange={handleDateChange}
          value={selectedDate ?? undefined}
          isCleanFilter={isCleanFilter}
        />
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 1,
          }}
        >
          <CustomButton
            title="Limpar filtros"
            variant={CustomButtonVariant.OUTLINED}
            onClick={handleClearFilters}
          />
          <CustomButton
            title="Consultar"
            variant={
              isFilterInvalid
                ? CustomButtonVariant.DISABLED
                : CustomButtonVariant.CONTAINED
            }
            onClick={handleConsult}
            disabled={isFilterInvalid}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
export default FilterTableIcmsSection;
